import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import {
  CONSENT_ACCEPT_ALL,
  CONSENT_REJECT_ALL,
  DEFAULT_CONSENT,
  readConsent,
  updateConsent,
} from "@/consent/consent";

export default function CookieBanner({ openSignal }) {
  const { lang, t } = useI18n();
  const [visible, setVisible] = useState(false);
  const [managing, setManaging] = useState(false);
  const [prefs, setPrefs] = useState({ ...DEFAULT_CONSENT });

  useEffect(() => {
    const stored = readConsent();
    if (!stored) {
      setVisible(true);
    } else {
      // Re-apply saved consent on every visit.
      updateConsent(stored);
      setPrefs(stored);
    }
  }, []);

  useEffect(() => {
    if (openSignal > 0) {
      const stored = readConsent();
      if (stored) setPrefs(stored);
      setManaging(true);
      setVisible(true);
    }
  }, [openSignal]);

  const handleAcceptAll = () => {
    updateConsent(CONSENT_ACCEPT_ALL);
    setVisible(false);
    setManaging(false);
  };

  const handleRejectAll = () => {
    updateConsent(CONSENT_REJECT_ALL);
    setVisible(false);
    setManaging(false);
  };

  const handleSave = () => {
    updateConsent({
      necessary: true,
      analytics: !!prefs.analytics,
      ads: !!prefs.ads,
      personalization: !!prefs.personalization,
    });
    setVisible(false);
    setManaging(false);
  };

  if (!visible) return null;

  return (
    <div className="zx-cookie" role="dialog" aria-live="polite" aria-label={t("cookie.title")}>
      <div className="zx-cookie__panel">
        {!managing ? (
          <>
            <div className="zx-cookie__content">
              <h3 className="zx-cookie__title">{t("cookie.title")}</h3>
              <p className="zx-cookie__body">
                {t("cookie.body")}{" "}
                <Link to={`/${lang}/cookies`} className="zx-cookie__link">
                  {t("cookie.learnMore")}
                </Link>
              </p>
            </div>
            <div className="zx-cookie__actions">
              <button type="button" className="zx-btn zx-btn--ghost" onClick={() => setManaging(true)}>
                {t("cookie.manage")}
              </button>
              <button type="button" className="zx-btn zx-btn--outline" onClick={handleRejectAll}>
                {t("cookie.rejectAll")}
              </button>
              <button type="button" className="zx-btn zx-btn--primary" onClick={handleAcceptAll}>
                {t("cookie.acceptAll")}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="zx-cookie__content">
              <h3 className="zx-cookie__title">{t("cookie.manage")}</h3>
              <ul className="zx-cookie__prefs">
                <li>
                  <div>
                    <strong>{t("cookie.catNecessary")}</strong>
                    <p>{t("cookie.catNecessaryBody")}</p>
                  </div>
                  <span className="zx-toggle is-locked">{t("cookie.required")}</span>
                </li>
                <li>
                  <div>
                    <strong>{t("cookie.catAnalytics")}</strong>
                    <p>{t("cookie.catAnalyticsBody")}</p>
                  </div>
                  <button
                    type="button"
                    className={`zx-toggle ${prefs.analytics ? "is-on" : ""}`}
                    onClick={() => setPrefs((p) => ({ ...p, analytics: !p.analytics }))}
                    aria-pressed={!!prefs.analytics}
                  >
                    <span className="zx-toggle__dot" />
                  </button>
                </li>
                <li>
                  <div>
                    <strong>{t("cookie.catAds")}</strong>
                    <p>{t("cookie.catAdsBody")}</p>
                  </div>
                  <button
                    type="button"
                    className={`zx-toggle ${prefs.ads ? "is-on" : ""}`}
                    onClick={() => setPrefs((p) => ({ ...p, ads: !p.ads }))}
                    aria-pressed={!!prefs.ads}
                  >
                    <span className="zx-toggle__dot" />
                  </button>
                </li>
                <li>
                  <div>
                    <strong>{t("cookie.catPersonalization")}</strong>
                    <p>{t("cookie.catPersonalizationBody")}</p>
                  </div>
                  <button
                    type="button"
                    className={`zx-toggle ${prefs.personalization ? "is-on" : ""}`}
                    onClick={() => setPrefs((p) => ({ ...p, personalization: !p.personalization }))}
                    aria-pressed={!!prefs.personalization}
                  >
                    <span className="zx-toggle__dot" />
                  </button>
                </li>
              </ul>
            </div>
            <div className="zx-cookie__actions">
              <button type="button" className="zx-btn zx-btn--ghost" onClick={() => setManaging(false)}>
                {t("cookie.back")}
              </button>
              <button type="button" className="zx-btn zx-btn--outline" onClick={handleRejectAll}>
                {t("cookie.rejectAll")}
              </button>
              <button type="button" className="zx-btn zx-btn--primary" onClick={handleSave}>
                {t("cookie.save")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
